import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './ExamBoosterEngine.css';

const ExamBoosterEngine = () => {
    const { t } = useTranslation(['exam_booster', 'subjects', 'common']);
    const [questions, setQuestions] = useState([]);
    const [examName, setExamName] = useState('');
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [showResults, setShowResults] = useState(false);
    const engineRef = useRef(null);
    const navigate = useNavigate();

    useEffect(() => {
        const storedQuestions = sessionStorage.getItem('booster_questions');
        const storedExam = sessionStorage.getItem('booster_exam');
        
        if (storedQuestions) {
            setQuestions(JSON.parse(storedQuestions));
            setExamName(storedExam || t('exam_booster:exam_booster.title', 'Exam Booster'));
        } else {
            navigate('/exam-booster');
        }
    }, [navigate, t]);

    useEffect(() => {
        if (window.renderMathInElement && engineRef.current) {
            window.renderMathInElement(engineRef.current, {
                delimiters: [
                    { left: "$$", right: "$$", display: true },
                    { left: "$", right: "$", display: false },
                    { left: "\\(", right: "\\)", display: false },
                    { left: "\\[", right: "\\]", display: true }
                ],
                throwOnError: false
            });
        }
    }, [questions, currentQuestionIndex, showResults, selectedAnswers]);

    const handleAnswerSelect = (questionIndex, option) => {
        if (showResults) return;
        setSelectedAnswers(prev => ({ ...prev, [questionIndex]: option }));
    };

    const handleNext = () => {
        if (currentQuestionIndex < questions.length - 1) {
            setCurrentQuestionIndex(prev => prev + 1);
        } else {
            setShowResults(true);
        }
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(prev => prev - 1);
        }
    };

    const calculateScore = () => {
        let score = 0;
        questions.forEach((q, idx) => {
            if (selectedAnswers[idx] === q.answer) {
                score++;
            }
        });
        return score;
    };

    if (questions.length === 0) {
        return (
            <div className="ebe-standalone-page">
                <div className="ebe-container" style={{ textAlign: 'center', marginTop: '100px' }}>
                    <h2 className="ebe-gradient-text">{t('exam_booster:exam_booster.engine.loading', 'Loading Practice Set...')}</h2>
                </div>
            </div>
        );
    }

    const currentQuestion = questions[currentQuestionIndex];

    return (
        <div className="ebe-standalone-page">
            <div className="ebe-container" ref={engineRef}>
                {!showResults ? (
                    <div className="ebe-mcq-engine">
                        <div className="ebe-engine-header">
                            <div>
                                <h2 className="ebe-gradient-text">{examName}</h2>
                                <p style={{ fontSize: '0.9rem', color: '#94a3b8', margin: '5px 0 0' }}>{t('exam_booster:exam_booster.engine.focusMode', 'Professional Focus Mode')}</p>
                            </div>
                            <div className="ebe-progress-indicator">
                                <span style={{ display: 'block', fontSize: '1.2rem', fontWeight: 'bold' }}>{t('exam_booster:exam_booster.engine.question', { current: currentQuestionIndex + 1, total: questions.length, defaultValue: `Question ${currentQuestionIndex + 1} / ${questions.length}` })}</span>
                                <div className="ebe-mini-progress-bar">
                                    <div className="ebe-progress-fill" style={{ width: `${((currentQuestionIndex + 1) / questions.length) * 100}%` }}></div>
                                </div>
                            </div>
                        </div>

                        <div className="ebe-question-card">
                            <div style={{ marginBottom: '20px' }}>
                                <span className="ebe-subject-tag">{t(`subjects:subjects.subjectNames.${currentQuestion.subject}`, currentQuestion.subject)}</span>
                            </div>

                            <div className="ebe-question-text">
                                {currentQuestion.question}
                            </div>

                            {currentQuestion.mathml && (
                                <div className="ebe-mathml-container" dangerouslySetInnerHTML={{ __html: currentQuestion.mathml }} />
                            )}

                            {currentQuestion.image_required === "yes" && (
                                <div className="ebe-image-container">
                                    <div className="ebe-placeholder-img">
                                        <p><strong>{t('exam_booster:exam_booster.engine.imageRequired', '[Image Required]')}</strong></p>
                                        <p>{currentQuestion.image_description}</p>
                                    </div>
                                </div>
                            )}

                            <div className="ebe-options-grid">
                                {currentQuestion.options.map((option, idx) => {
                                    const optionLetter = String.fromCharCode(65 + idx);
                                    return (
                                        <button
                                            key={idx}
                                            className={`ebe-option-btn ${selectedAnswers[currentQuestionIndex] === optionLetter ? 'selected' : ''}`}
                                            onClick={() => handleAnswerSelect(currentQuestionIndex, optionLetter)}
                                        >
                                            <span className="ebe-option-letter">{optionLetter}.</span>
                                            {option}
                                        </button>
                                    );
                                })}
                            </div>

                            <div style={{ marginTop: '40px', display: 'flex', justifyContent: 'space-between' }}>
                                <button 
                                    className="ebe-btn-prev" 
                                    onClick={handlePrevious}
                                    disabled={currentQuestionIndex === 0}
                                >
                                    {t('exam_booster:exam_booster.engine.prev', 'Previous')}
                                </button>
                                <button 
                                    className="ebe-btn-next" 
                                    onClick={handleNext}
                                >
                                    {currentQuestionIndex === questions.length - 1 ? t('exam_booster:exam_booster.engine.finish', 'Finish & Show Result') : t('exam_booster:exam_booster.engine.next', 'Next Question')}
                                </button>
                            </div>
                        </div>

                        <div style={{ marginTop: '30px', textAlign: 'center' }}>
                             <button 
                                onClick={() => {
                                    if(window.confirm(t('exam_booster:exam_booster.engine.quitConfirm', "Are you sure you want to quit? All progress will be lost."))) {
                                        navigate('/exam-booster');
                                    }
                                }}
                                style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.9rem' }}
                             >
                                {t('exam_booster:exam_booster.engine.quitBtn', 'Quit Session')}
                             </button>
                        </div>
                    </div>
                ) : (
                    <div className="ebe-results-section" style={{ textAlign: 'center' }}>
                        <div className="ebe-score-card">
                            <h1 className="ebe-gradient-text" style={{ fontSize: '2.5rem' }}>{t('exam_booster:exam_booster.engine.completed', 'Practice Completed!')}</h1>
                            <div className="ebe-score-circle">
                                {calculateScore()} / {questions.length}
                            </div>
                            <p style={{ color: '#94a3b8', fontSize: '1.2rem' }}>{t('exam_booster:exam_booster.engine.wellDone', 'Well done! Review your performance below.')}</p>
                        </div>
                        
                        <div style={{ textAlign: 'left', marginTop: '40px' }}>
                            <h2 style={{ marginBottom: '30px', borderBottom: '1px solid #1f2937', paddingBottom: '10px' }}>{t('exam_booster:exam_booster.engine.analysis', 'Question Analysis')}</h2>
                            {questions.map((q, idx) => (
                                <div key={idx} className="ebe-question-card" style={{ borderLeft: q.answer === selectedAnswers[idx] ? '6px solid #00ffaa' : '6px solid #ff0064' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                                        <span className="ebe-subject-tag">{t(`subjects:subjects.subjectNames.${q.subject}`, q.subject)}</span>
                                        <span style={{ color: q.answer === selectedAnswers[idx] ? '#00ffaa' : '#ff0064', fontWeight: 'bold' }}>
                                            {q.answer === selectedAnswers[idx] ? `✓ ${t('exam_booster:exam_booster.engine.correct', 'CORRECT')}` : `✗ ${t('exam_booster:exam_booster.engine.incorrect', 'INCORRECT')}`}
                                        </span>
                                    </div>
                                    <p style={{ fontSize: '1.2rem', marginBottom: '20px' }}><strong>Q{idx + 1}:</strong> {q.question}</p>
                                    <div style={{ padding: '20px', background: 'rgba(255,255,255,0.03)', borderRadius: '12px', marginBottom: '20px' }}>
                                        <div style={{ marginBottom: '10px' }}>{t('exam_booster:exam_booster.engine.yourAnswer', 'Your Answer')}: <span style={{ fontWeight: 'bold', color: selectedAnswers[idx] ? '#fff' : '#6b7280' }}>{selectedAnswers[idx] || t('exam_booster:exam_booster.engine.notAnswered', 'Not Answered')}</span></div>
                                        <div>{t('exam_booster:exam_booster.engine.correctAnswer', 'Correct Answer')}: <span style={{ color: '#00ffaa', fontWeight: 'bold' }}>{q.answer}</span></div>
                                    </div>
                                    <div className="ebe-explanation-box">
                                        <strong style={{ display: 'block', marginBottom: '8px' }}>{t('exam_booster:exam_booster.engine.explanation', 'Expert Explanation')}:</strong>
                                        {q.explanation}
                                    </div>
                                </div>
                            ))}
                        </div>

                        <button className="ebe-btn-next" onClick={() => navigate('/exam-booster')} style={{ marginTop: '40px', padding: '15px 60px' }}>
                            {t('exam_booster:exam_booster.engine.backToGenerator', 'Back to Generator')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ExamBoosterEngine;
